import React, { useState, useRef, useEffect, useCallback } from "react"
import axios from "axios"
import Button from "@yumgmbh/gatsby-theme-yum-components/src/components/atoms/Button"
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api"
import mapStyles from "../../utilities/mapStyles"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronDown,
  faClock,
  faEnvelope,
  faFax,
  faG,
  faGlobe,
  faLocationCrosshairs,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faShop,
  faXmarkLarge,
} from "@fortawesome/sharp-light-svg-icons"

import { useTranslation } from "react-i18next"
import Link from "@yumgmbh/gatsby-theme-yum-components/src/components/atoms/Link"
import {
  faCircleExclamation,
  faSpinner,
} from "@fortawesome/sharp-solid-svg-icons"

const getNexmartDealer = async (filterOptions) => {
  let res = axios
    .post(
      process.env.GATSBY_NEXMART_DEALER_SEARCH_FUNCTION_URL,
      {
        lat: filterOptions.lat,
        lng: filterOptions.lng,
        distance: filterOptions.distance,
      },
      {
        headers: {
          Authorization:
            process.env.GATSBY_NEXMART_DEALER_SEARCH_FUNCTION_TOKEN,
          "Content-Type": "application/json",
        },
      }
    )
    .then((response) => {
      if (response.data) {
        return {
          status: "success",
          dealers: response.data,
        }
      } else {
        return {
          status: "error",
          dealers: null,
        }
      }
    })
    .catch((error) => {
      return {
        status: "error",
        dealers: null,
      }
    })

  return res
}

const mapOptions = {
  styles: mapStyles,
  disableDefaultUI: true,
}

const containerStyle = {
  display: "block",
  width: "100%",
  height: "100%",
}

let center = {
  lat: 50.10656477518081,
  lng: 8.676380258220476,
}

const formatDistance = (distance) => {
  if (distance < 1000) {
    return distance + " m"
  } else if (distance >= 1000) {
    return (distance / 1000).toFixed(1).replace(".", ",") + " km"
  } else {
    return "formatedDistance"
  }
}

// ++++++++++++++++++++++++++++++++++++++++++ DealerSearch ++++++++++++++++++++++++++++++++++++++++++

const NexmartDealerSearch = ({
  className,
  handleSearchSubmit,
  searchQuery,
  handleSearchInput,
  searchRef,
  clearSearchInput,
  handleLocationDetection,
}) => {
  const { t } = useTranslation()
  if (
    process.env.GATSBY_DEALER_SEARCH_CENTER &&
    process.env.GATSBY_DEALER_SEARCH_CENTER.split(",").length === 2
  ) {
    center.lat = parseFloat(
      process.env.GATSBY_DEALER_SEARCH_CENTER.split(",")[0]
    )
    center.lng = parseFloat(
      process.env.GATSBY_DEALER_SEARCH_CENTER.split(",")[1]
    )
  }
  return (
    <div
      className={`${
        className?.length > 0 ? className : ""
      } lg:w-[calc(50%-2rem)]`}
    >
      <form
        className="relative flex w-full bg-ro-white"
        onSubmit={handleSearchSubmit}
      >
        <button
          type="submit"
          name="search-by-text"
          className="absolute left-0 z-20 flex items-center justify-center w-12 h-12"
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} size="1x" className="" />
        </button>
        <input
          className="w-full px-12 py-3 bg-transparent border-b text-ro-gray-900 placeholder-ro-gray-300 border-ro-gray-400"
          type="text"
          name="location_query"
          placeholder={t("generic.dealer_map.search_placeholder")}
          value={searchQuery}
          onChange={handleSearchInput}
          ref={searchRef}
        />

        {searchQuery !== "" ? (
          <button
            className="absolute right-0 z-20 flex items-center justify-center w-12 h-12"
            onClick={() => clearSearchInput()}
          >
            <FontAwesomeIcon icon={faXmarkLarge} size="1x" className="" />
          </button>
        ) : (
          <button
            className="absolute right-0 z-20 flex items-center justify-center w-12 h-12"
            onClick={() => {
              handleLocationDetection()
            }}
          >
            <FontAwesomeIcon
              icon={faLocationCrosshairs}
              size="1x"
              className=""
            />
          </button>
        )}
      </form>
    </div>
  )
}

// ++++++++++++++++++++++++++++++++++++++++++ DealerDetailsCard ++++++++++++++++++++++++++++++++++++++++++

const NexmartDealerDetailsCard = ({
  className,
  marker,
  showCenterMarker,
  setSelectedMarker,
  panTo,
  gridMode = false,
  modalMode = false,
  modalModeScrollUpFunction,
}) => {
  const { t } = useTranslation()
  const [showCollapsedInfos, setShowCollapsedInfos] = useState(
    gridMode ? false : true
  )

  const buildMapsDestinationString = (address, postcode, city) => {
    return encodeURIComponent(`${address},${postcode},${city}`)
  }

  let hasContactInfos =
    marker.company?.address?.phone?.length > 0 ||
    marker.company?.address?.fax?.length > 0 ||
    marker.company?.address?.email?.length > 0 ||
    marker.company?.website?.length > 0

  let hasOpeningHours = marker.company?.shopHours[0]?.description?.length

  return (
    <div
      className={`bg-ro-white flex flex-col ${gridMode ? "" : "h-full"} ${
        className ? className : ""
      }`}
    >
      <div className="p-4">
        <span className="block headline headline-h3">
          {marker.company.name}
        </span>
        {showCenterMarker && (
          <span className="block mt-2">{formatDistance(marker.distance)}</span>
        )}
      </div>
      <div className="px-4 overflow-y-auto">
        <div className="pt-4 pb-8 border-b border-ro-gray-100">
          <Button
            btnType="outlineRed"
            className="flex items-center justify-between w-full"
            onClick={() => {
              if (!modalMode && gridMode) {
                window.scrollTo(0, 50)
              }
              if (modalMode && gridMode) {
                modalModeScrollUpFunction()
              }
              setSelectedMarker(marker.company.id)
              panTo({
                lat: parseFloat(marker.company.address.latitude),
                lng: parseFloat(marker.company.address.longitude),
              })
            }}
          >
            {t("generic.dealer_map.show_on_map")}
            <FontAwesomeIcon icon={faLocationDot} size="1x" />
          </Button>
          <Button
            btnType="outlineRed"
            className="flex items-center justify-between w-full mt-4"
            link={`https://www.google.de/maps/dir/?api=1&destination=${buildMapsDestinationString(
              marker.company?.address?.street,
              marker.company?.address?.zip,
              marker.company?.address?.city
            )}`}
            id="JumpToLocalDealer"
          >
            {t("generic.dealer_map.process_route")}
            <FontAwesomeIcon icon={faG} size="1x" />
          </Button>
        </div>
        <div className="flex py-4">
          <span>
            <FontAwesomeIcon
              icon={faShop}
              size="1x"
              className="w-5 h-5 mt-0.5"
            />
          </span>
          <p className="block ml-2">
            <span className="block">{marker.company?.address?.street}</span>
            <span className="block">
              {marker.company?.address?.zip} {marker.company?.address?.city}
            </span>
            <span className="block">{marker.company?.address?.country}</span>
          </p>
        </div>

        <div className={`overflow-hidden ${showCollapsedInfos ? "" : "h-0"}`}>
          {hasContactInfos && (
            <div className="py-4 border-t border-ro-gray-100">
              {marker.company?.address?.phone?.length > 0 && (
                <div className="flex py-4">
                  <span>
                    <FontAwesomeIcon
                      icon={faPhone}
                      size="1x"
                      className="w-5 h-5 mt-0.5"
                    />
                  </span>
                  <span className="block ml-2">
                    {marker.company?.address?.phone}
                  </span>
                </div>
              )}
              {marker.company?.address?.fax?.length > 0 && (
                <div className="flex py-4">
                  <span>
                    <FontAwesomeIcon
                      icon={faFax}
                      size="1x"
                      className="w-5 h-5 mt-0.5"
                    />
                  </span>
                  <span className="block ml-2">
                    {marker.company?.address?.fax}
                  </span>
                </div>
              )}
              {marker.company?.address?.email?.length > 0 && (
                <div className="flex py-4">
                  <span>
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      size="1x"
                      className="w-5 h-5 mt-0.5"
                    />
                  </span>
                  <span className="block ml-2">
                    {marker.company?.address?.email}
                  </span>
                </div>
              )}
              {marker.company?.website?.length > 0 && (
                <div className="flex py-4">
                  <span>
                    <FontAwesomeIcon
                      icon={faGlobe}
                      size="1x"
                      className="w-5 h-5 mt-0.5"
                    />
                  </span>
                  <Link
                    link={
                      marker.company.website?.indexOf("https://") === -1 &&
                      marker.company.website?.indexOf("http://") === -1
                        ? "https://" + marker.company.website
                        : marker.company.website
                    }
                    className="ml-2 underline-offset-4 hover:underline active:underline"
                  >
                    {marker.company?.website}
                  </Link>
                </div>
              )}
            </div>
          )}
          {hasOpeningHours && (
            <div className="py-4 border-t border-ro-gray-100">
              <div className="flex py-4">
                <span>
                  <FontAwesomeIcon
                    icon={faClock}
                    size="1x"
                    className="w-5 h-5 mt-0.5"
                  />
                </span>
                <div
                  className="w-full ml-2 "
                  dangerouslySetInnerHTML={{
                    __html:
                      "<table class='w-full table-auto'><tbody><tr><td>" +
                      marker.company?.shopHours[0]?.description
                        .replaceAll("\t", "</td><td>")
                        .replaceAll("\n", "</td><tr><td>") +
                      "</td></tbody></table>",
                  }}
                ></div>
              </div>
            </div>
          )}
        </div>
        {gridMode && (
          <div className="w-full h-12 text-right">
            {(hasContactInfos || hasOpeningHours) && (
              <button
                className="-mr-4 border btn border-ro-gray-300 hover:border-ro-gray-75 hover:bg-ro-gray-75 active:bg-ro-gray-200 active:border-ro-gray-200"
                onClick={() => {
                  setShowCollapsedInfos(!showCollapsedInfos)
                }}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="1x"
                  className={`transform ${
                    showCollapsedInfos ? "-rotate-180" : ""
                  } `}
                />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ++++++++++++++++++++++++++++++++++++++++++ DealerMap ++++++++++++++++++++++++++++++++++++++++++

const NexmartDealerMap = ({
  setQueryString,
  queryString,
  modalMode,
  modalModeScrollUpFunction,
}) => {
  const { i18n } = useTranslation()
  const [mapLoaded, setMapLoaded] = useState(false)
  const [visibleMarkers, setVisibleMarkers] = useState(null)
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [showCenterMarker, setShowCenterMarker] = useState(false)
  const [error, setError] = useState(null)
  const mapRef = useRef()
  const searchRef = useRef()
  const zoom = parseInt(process.env.GATSBY_DEALER_SEARCH_INIT_ZOOM) || 11

  const { t } = useTranslation()

  let defaultDealerFilterOptions = {}

  // state for dealer filter options
  const [dealerFilterOptions, setDealerFilterOptions] = useState(
    defaultDealerFilterOptions
  )

  // state for dealer data
  const [dealerData, setDealerData] = useState(null)

  // refetch dealers if dealerFilterOptions was changed
  useEffect(() => {
    getNexmartDealer(dealerFilterOptions).then((res) => {
      if (res.status === "success") {
        setDealerData(
          res.dealers
            .sort((a, b) => {
              if (a.distance < b.distance) {
                return -1
              } else if (a.distance > b.distance) {
                return 1
              }
              return 0
            })
            .filter((dealer) => dealer.distance < 50001)
        )
      }
    })
  }, [dealerFilterOptions])

  useEffect(() => {
    if (dealerData && dealerData.length > 0) {
      getVisibleMarkers()
    }
  }, [dealerData])

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY,
    region: process.env.GATSBY_GLOBAL_PATH_PREFIX.toLocaleUpperCase(),
    language: i18n.language,
  })

  const handleLocationDetection = () => {
    const geo = navigator.geolocation
    setError("generic.dealer_map.errors.UserdeniedGeolocation")
    geo.getCurrentPosition((position) => {
      setError(null)
      setDealerFilterOptions({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        distance: 50000,
      })
      geoByCoords(position.coords.latitude, position.coords.longitude)
    })
  }

  const geoByCoords = useCallback(
    (lat, lng) => {
      if (mapLoaded) {
        const geocoder = new google.maps.Geocoder()
        geocoder.geocode(
          { location: { lat: lat, lng: lng } },
          async (results) => {
            try {
              const result = results[0]
              const address = result.formatted_address
              setSearchQuery(address)
              panTo({ lat: lat, lng: lng })
              setShowCenterMarker(true)
              mapRef.current.setZoom(11)
            } catch (error) {
              console.log(error)
            }
          }
        )
      }
    },
    [mapLoaded]
  )

  const [searchQuery, setSearchQuery] = useState("")
  const handleSearchSubmit = (event) => {
    event.preventDefault()
    setSelectedMarker(null)
    if (searchQuery && searchQuery !== "") {
      let searchObj = { address: decodeURIComponent(searchQuery) }
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode(searchObj, async (results) => {
        try {
          const result = results[0]
          const address = result.formatted_address
          const lat = await result.geometry.location.lat()
          const lng = await result.geometry.location.lng()

          // refetch dealers
          setDealerFilterOptions({
            lat: lat,
            lng: lng,
            distance: 50000,
          })

          panTo({ lat: lat, lng: lng })
          mapRef.current.setZoom(11)
          setShowCenterMarker(true)
        } catch (error) {
          console.error(error)
        }
      })
    }
  }
  const handleSearchInput = (event) => {
    setSearchQuery(event.target.value)
  }

  const clearSearchInput = () => {
    searchRef.current.value = ""
    setSearchQuery("")
  }

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // GoogleMap Functions
  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  const panTo = ({ lat, lng }) => {
    mapRef.current.panTo({ lat, lng })
  }

  const onMapLoad = useCallback((map) => {
    mapRef.current = map
    setMapLoaded(true)
  }, [])

  // get all markers in current bounds when map is idle
  let markers = []
  const getVisibleMarkers = () => {
    markers.length > 0 && markers.splice(0, markers.length)
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds()
      dealerData?.map((marker) => {
        const markerPosition = {
          lat: parseFloat(marker.company.address.latitude),
          lng: parseFloat(marker.company.address.longitude),
        }
        if (bounds && bounds.contains(markerPosition)) {
          markers.push(marker)
        }
      })
      setVisibleMarkers(markers)
    }
  }

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // GoogleMap Styling
  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  const locationMarkerIcon = () => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 32 44" fill="none">
        <g>
          <path d="M32 17.0625C32 26.4688 16 43.9375 16 43.9375C16 43.9375 0 26.4688 0 17.0625C0 8.16016 7.16667 0.9375 16 0.9375C24.8333 0.9375 32 8.16016 32 17.0625Z" fill="#161616" stroke="#ffffff" strokeWidth="1"/>
        </g>
      </svg>`
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  }

  const dealerMarkerIcon = () => {
    const svg = `
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z" fill="#E0000F" stroke="#ffffff" strokeWidth="1"/>
        <path d="M11 16C11 14.6739 11.5268 13.4021 12.4645 12.4645C13.4021 11.5268 14.6739 11 16 11C17.3261 11 18.5979 11.5268 19.5355 12.4645C20.4732 13.4021 21 14.6739 21 16C21 17.3261 20.4732 18.5979 19.5355 19.5355C18.5979 20.4732 17.3261 21 16 21C14.6739 21 13.4021 20.4732 12.4645 19.5355C11.5268 18.5979 11 17.3261 11 16Z" fill="white"/>
      </svg>
      `
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  }

  let containerStyleSettings = Object.assign({}, containerStyle)

  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // MarkUp
  // +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  return (
    <>
      <section className="w-full bg-ro-gray-50">
        <div
          className={modalMode ? "px-0 md:px-8 md:pb-8" : "block-w-container"}
        >
          {error && (
            <div className="relative py-[0.6875rem] pl-12 pr-4 md:mb-4 border-r border-y bg-[#FFF1F2] border-[#f6a9ae]">
              <span className="absolute left-0 -inset-y-[1px] w-[3px] bg-ro-red-500"></span>
              <span className="absolute flex top-4 left-4">
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  size="1x"
                  className="text-ro-red-500"
                />
              </span>
              {t(error)}
            </div>
          )}
          <div
            className={`w-full flex flex-col md:flex-row relative overflow-hidden ${
              modalMode ? "md:h-[40rem]" : ""
            }  ${
              containerStyleSettings.height === "100svh"
                ? " h-screen"
                : "h-full"
            }`}
          >
            {/* ################## Loading Spinner ################## */}
            {!isLoaded && (
              <div className="absolute inset-0 z-[25] flex items-center justify-center bg-ro-gray-100 text-ro-red-500 ">
                <FontAwesomeIcon
                  icon={faSpinner}
                  size="1x"
                  className="w-16 h-16 animate-spin"
                />
              </div>
            )}
            {/* ################## END Loading Spinner ################## */}
            {isLoaded && (
              <>
                {/* ################## Searchfield ################## */}
                <NexmartDealerSearch
                  className="z-10 md:absolute left-4 top-8 right-4 lg:left-8 md:right-auto"
                  handleSearchSubmit={handleSearchSubmit}
                  searchQuery={searchQuery}
                  handleSearchInput={handleSearchInput}
                  searchRef={searchRef}
                  clearSearchInput={clearSearchInput}
                  handleLocationDetection={handleLocationDetection}
                />
                {/* ################## END Searchfield ################## */}

                {/* ################## GoogleMap ################## */}
                <div
                  className={
                    modalMode
                      ? "w-full aspect-square h-auto md:aspect-auto md:h-[40rem]"
                      : "w-[100dvw] h-[100dvw] md:h-[80dvh] md:w-full"
                  }
                >
                  <GoogleMap
                    mapContainerStyle={containerStyleSettings}
                    center={center}
                    zoom={zoom}
                    options={mapOptions}
                    onLoad={onMapLoad}
                    onIdle={getVisibleMarkers}
                  >
                    {searchQuery !== "" && showCenterMarker && (
                      <Marker
                        icon={locationMarkerIcon()}
                        position={{
                          lat: parseFloat(dealerFilterOptions.lat),
                          lng: parseFloat(dealerFilterOptions.lng),
                        }}
                      />
                    )}
                    {dealerData?.map((marker, index) => {
                      return (
                        <Marker
                          key={marker.company.id}
                          position={{
                            lat: parseFloat(marker.company.address.latitude),
                            lng: parseFloat(marker.company.address.longitude),
                          }}
                          icon={dealerMarkerIcon()}
                          onClick={() => {
                            setSelectedMarker(marker.company.id)
                            panTo({
                              lat: parseFloat(marker.company.address.latitude),
                              lng: parseFloat(marker.company.address.longitude),
                            })
                          }}
                        >
                          {marker.company.id === selectedMarker ? (
                            <InfoWindow
                              onCloseClick={() => {
                                setSelectedMarker(null)
                              }}
                              className="bg-ro-black text-ro-white"
                              options={{ headerDisabled: true }}
                            >
                              <div>
                                <div className="font-bold">
                                  {marker.company.name}
                                </div>
                                {showCenterMarker && (
                                  <div>{formatDistance(marker.distance)}</div>
                                )}
                              </div>
                            </InfoWindow>
                          ) : null}
                        </Marker>
                      )
                    })}
                  </GoogleMap>
                </div>
                {/* ################## END GoogleMap ################## */}
              </>
            )}
            {/* ################## Sidebar: Selected Dealer Details ################## */}
            <div
              className={`transition-all transform overflow-hidden w-full ${
                modalMode ? "md-[40rem]" : "md:h-[80dvh]"
              } ${
                selectedMarker !== null
                  ? "md:w-[calc(100%-4rem)] lg:w-[calc(50%-3rem)]"
                  : "h-0 md:w-0 "
              } shadow-lg bg-ro-white`}
            >
              <div className="relative h-full">
                <button
                  className="absolute top-0 right-0 flex items-center justify-center w-12 h-12 bg-ro-white"
                  onClick={() => {
                    setSelectedMarker(null)
                  }}
                >
                  <FontAwesomeIcon
                    icon={faXmarkLarge}
                    size="1x"
                    className="w-4 h-4"
                  />
                </button>
                {selectedMarker !== null &&
                  dealerData
                    .filter((marker) => marker.company.id === selectedMarker)
                    .map((marker, index) => {
                      return (
                        <NexmartDealerDetailsCard
                          marker={marker}
                          showCenterMarker={showCenterMarker}
                          setSelectedMarker={setSelectedMarker}
                          panTo={panTo}
                        />
                      )
                    })}
              </div>
            </div>
            {/* ################## END Sidebar: Selected Dealer Details ################## */}
          </div>
        </div>
      </section>
      {/* ################## Visible Dealer Details ################## */}
      {visibleMarkers?.length > 0 && (
        <section>
          <div className="bg-ro-gray-50">
            <div
              className={`pb-block pt-2 md:pt-6 ${
                modalMode ? "px-8" : "block-w-container"
              }`}
            >
              <div className="flex flex-row flex-wrap -mb-8 -mx-grid ">
                {visibleMarkers &&
                  visibleMarkers?.map((marker, index) => (
                    <div
                      className="w-full mb-8 md:w-1/2 lg:w-1/3 px-grid"
                      key={marker.company.id}
                    >
                      <NexmartDealerDetailsCard
                        gridMode={true}
                        marker={marker}
                        showCenterMarker={showCenterMarker}
                        setSelectedMarker={setSelectedMarker}
                        panTo={panTo}
                        modalMode={modalMode}
                        modalModeScrollUpFunction={modalModeScrollUpFunction}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>
      )}
      {/* ################## END Visible Dealer Details ################## */}
      {/* <section>
        <div className="py-block block-w-container">
          <h1 className="py-4">Dealer Data: {dealerData?.length} entries</h1>
          {JSON.stringify(dealerData)}
        </div>
      </section> */}
    </>
  )
}

export default NexmartDealerMap
